// @ts-strict-ignore
import {
  FetchLoggedOutError,
  FetchNotFoundError,
  FetchServerError,
  FetchUnauthorizedError,
  FetchPasswordInvalidError,
  FetchRequestError,
  FetchSsoError,
  FetchElevatedAccessError,
} from "@lib/errors";
import { FetchUserEncodedError, isUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import { FetchUserError, isWarnJson } from "@lib/errors/FetchUserError";

import {
  ELEVATED_ACCESS_REQUIRED_STATUS_CODE,
  MUST_CHANGE_PASSWORD_STATUS_CODE,
  SANDBOX_TOS_STATUS_CODE,
  SSO_REQUIRED_STATUS_CODE,
  UNIDENTIFIED_ERROR,
} from "../constants";

/**
 * The machine-readable denial code from the canonical Braze::API error envelope
 * (`{errors: [{status_code, error_code, message, ...}]}`), or undefined for any other response
 * shape. Legacy endpoints never emit this envelope, so branching on the returned code can never
 * change their handling. Codes are defined in `shared_code/domains/api/public/braze/api/errors.rb`
 * (`Braze::API::Errors::DenialCodes`).
 */
function brazeApiErrorCode(responseJson: Record<string, unknown>): string | undefined {
  const errors = responseJson?.errors;
  if (!Array.isArray(errors)) {
    return undefined;
  }
  const code = errors[0]?.error_code;
  return typeof code === "string" ? code : undefined;
}

/**
 * Given the response from a fetch() call, extract an appropriate error class, which can be used to
 * intelligently handle the error locally, or displayed in a modal by the ErrorBoundary class
 *
 * this is the React version of code in `app/assets/js/dashboard/dashboard-require-init.ko.js.coffee` which
 * applies the same logic to $.ajax
 *
 * If you add a new error here, be sure to augment the `FetchErrorModal` in `app/javascript/src/components/ErrorBoundary/FetchErrorModal/FetchErrorModal.tsx`.
 * The `FetchErrorModalBody`, `FetchErrorModalTitle`, and `getFetchErrorModalAction` helpers may all need updating.
 */
export async function extractError(
  response: Response,
  redirectToSignin = () => {
    window.location.href = "/developers/sign_in";
  }
): Promise<
  | FetchLoggedOutError
  | FetchNotFoundError
  | FetchServerError
  | FetchUnauthorizedError
  | FetchPasswordInvalidError
  | FetchUserError
  | FetchRequestError
> {
  let detailedError: string;
  let responseJson: Record<string, unknown>;

  try {
    detailedError = await response.text();
    // eslint-disable-next-line no-empty
  } catch {}

  try {
    responseJson = await JSON.parse(detailedError);
    // eslint-disable-next-line no-empty
  } catch {}
  const statusCode = response.status;
  const statusText = response.statusText;

  if (isUserEncodedError(responseJson)) {
    return new FetchUserEncodedError(statusCode, responseJson.message, responseJson);
  }

  if (isWarnJson(responseJson)) {
    return new FetchUserError(statusCode, responseJson.warn, responseJson, detailedError);
  }

  if (response.ok) {
    throw new Error("internal error: extractError should not be called with a valid fetch() response");
  }

  if (statusCode === 500) {
    // On the server side, we control 500s to just return the error message, but there are edge cases (eg the
    // middleware throws the exception) where we might get a fully rendered 500 page.  To ensure that we don't
    // display unrendered html to the user, detect whether we've been given back a full html page or just the error
    // message.
    if (statusText.indexOf("<!DOCTYPE html>") >= 0) {
      return new FetchServerError(500, "An unexpected error has occurred", responseJson);
    } else if (detailedError?.indexOf("Request timed out") >= 0) {
      return new FetchServerError(500, "Request timed out", responseJson);
    }
    return new FetchServerError(500, statusText, responseJson);
  } else if (statusCode === 503 || statusCode === 504) {
    // A Braze::API company-availability denial rides 503 but is not a timeout — retrying
    // immediately will not help, so it gets its own copy.
    if (brazeApiErrorCode(responseJson) === "service_unavailable.company") {
      return new FetchServerError(
        statusCode,
        "This account is temporarily unavailable. Please try again shortly. If you continue to receive this error contact support directly",
        responseJson
      );
    }
    // For NGINX timeouts (504) and First byte timeouts (503), show a modal explaining the request timed out.
    return new FetchServerError(
      statusCode,
      "Your request timed out. Please try refreshing the page. If you continue to receive this error contact support directly",
      responseJson
    );
  } else if (statusCode > 500) {
    return new FetchServerError(statusCode, "An unexpected error has occurred", responseJson);
  } else if (statusCode === 401) {
    // A Braze::API IP-allowlist or company-ban denial re-evaluates on every request, so
    // redirecting to sign-in would just loop the user straight back here (legacy showed this same
    // account message instead of forcing a re-login). unauthorized.credential_type_not_accepted
    // (below) skips the redirect too. Every other 401, including unauthorized.stale_impersonation,
    // keeps the sign-in redirect as its recovery.
    const enveloped401 = brazeApiErrorCode(responseJson);
    if (enveloped401 === "unauthorized.ip_not_allowed" || enveloped401 === "unauthorized.company_banned") {
      return new FetchUnauthorizedError(
        401,
        "There is something wrong with your account. Please contact your Account Administrator.",
        responseJson
      );
    }
    // The dashboard sends its session cookie and no bearer token, so this code means that the page
    // called a Braze::API route whose policy does not accept a dashboard session. Signing in again
    // sends the same type of credential, so the redirect cannot fix it.
    if (enveloped401 === "unauthorized.credential_type_not_accepted") {
      return new FetchUnauthorizedError(
        401,
        "This page could not load some of its data. Signing in again does not fix it. If you keep seeing this message, contact support.",
        responseJson
      );
    }
    // trigger a redirect to the login page
    redirectToSignin();
    return new FetchLoggedOutError("Unauthorized access attempted");
  } else if (statusCode === 404) {
    return new FetchNotFoundError(
      404,
      "Oops! It looks like we made a mistake. The page you requested could not be found.",
      responseJson
    );
  } else if (statusCode === 403) {
    const enveloped403 = brazeApiErrorCode(responseJson);
    if (enveloped403 === "forbidden.csrf") {
      // A stale CSRF token, not a permission problem. No automatic retry: brazeFetch reads the
      // token from the page's meta tag, which a retry would re-read unchanged — only a page
      // refresh mints a fresh one.
      return new FetchUnauthorizedError(
        403,
        "Your session token expired. Refresh the page and try again.",
        responseJson
      );
    }
    if (enveloped403 === "forbidden.read_only_impersonation") {
      return new FetchUnauthorizedError(
        403,
        "You are impersonating in read-only mode, so this action is unavailable.",
        responseJson
      );
    }
    return new FetchUnauthorizedError(
      403,
      "Oops! You don't have the permissions for the requested action.",
      responseJson
    );
  } else if (statusCode === SANDBOX_TOS_STATUS_CODE) {
    return new FetchRequestError(
      SANDBOX_TOS_STATUS_CODE,
      "Please accept the sandbox Terms of Service to continue.",
      responseJson
    );
  } else if (statusCode === MUST_CHANGE_PASSWORD_STATUS_CODE) {
    return new FetchPasswordInvalidError(
      MUST_CHANGE_PASSWORD_STATUS_CODE,
      "Your administrator has updated your password requirements, or your password has expired."
    );
  } else if (statusCode === SSO_REQUIRED_STATUS_CODE) {
    return new FetchSsoError(
      SSO_REQUIRED_STATUS_CODE,
      "Your administrator now requires SSO authentication.",
      responseJson
    );
  } else if (statusCode === ELEVATED_ACCESS_REQUIRED_STATUS_CODE) {
    return new FetchElevatedAccessError(
      ELEVATED_ACCESS_REQUIRED_STATUS_CODE,
      "Identity verification with SSO or 2FA required.",
      responseJson
    );
  } else if (statusCode === UNIDENTIFIED_ERROR && brazeApiErrorCode(responseJson) === undefined) {
    // This refresh copy is for ApplicationController#render_409, which answers a missing or invalid app group ID,
    // and a reload fixes that. Many legacy endpoints also send a JSON 409, so every 409 without the Braze::API
    // envelope keeps this copy. A Braze::API 409 carries a domain error_code, such as a duplicate name, that a
    // reload cannot fix. It falls through to the generic error below, like an enveloped 400 or 422.
    return new FetchRequestError(
      UNIDENTIFIED_ERROR,
      "Oops! An unspecified error has occurred. Refresh your page and everything should be fine! If you keep seeing this message, please reach out to support.",
      responseJson
    );
  }

  // unexpected error
  return new FetchRequestError(statusCode, "An unexpected error has occurred", responseJson, detailedError);
}
